<template>
  <div class="fan-presence-editor space-y-5">
    <section class="fan-presence-editor__section">
      <h4 class="fan-presence-editor__section-title">{{ tt('Delays', 'Zakasnitve') }}</h4>
      <div class="fan-presence-editor__grid">
        <label class="fan-presence-editor__number">
          <span>{{ tt('Startup delay', 'Zakasnitev zagona') }}</span>
          <input
            type="number"
            min="0"
            step="1"
            :value="config.startup_delay_seconds"
            @change="
              update({ startup_delay_seconds: Math.max(0, Number(($event.target as HTMLInputElement).value) || 0) })
            " />
          <span>{{ tt('sec', 'sek') }}</span>
        </label>
        <label class="fan-presence-editor__number">
          <span>{{ tt('Operating delay', 'Zakasnitev delovanja') }}</span>
          <input
            type="number"
            min="0"
            step="10"
            :value="config.operating_delay_seconds"
            @change="
              update({ operating_delay_seconds: Math.max(0, Number(($event.target as HTMLInputElement).value) || 0) })
            " />
          <span>{{ tt('sec', 'sek') }}</span>
        </label>
      </div>
      <p class="fan-presence-editor__hint">
        {{ tt('Presence must persist for the', 'Prisotnost mora vztrajati') }}
        <b>{{ tt('startup delay', 'zakasnitev zagona') }}</b>
        {{
          tt(
            'before the fan runs. After presence is lost, the fan keeps running for the',
            'preden ventilator steče. Ko prisotnost izgine, ventilator deluje še',
          )
        }}
        <b>{{ tt('operating delay', 'zakasnitev delovanja') }}</b>
        {{ tt('before returning to the idle speed.', 'preden se vrne na hitrost mirovanja.') }}
      </p>
    </section>

    <section class="fan-presence-editor__section">
      <h4 class="fan-presence-editor__section-title">{{ tt('Speeds', 'Hitrosti') }}</h4>

      <div class="fan-presence-editor__speed-slider">
        <div class="fan-presence-editor__slider-head">
          <span class="fan-presence-editor__label">{{ tt('Idle speed', 'Hitrost mirovanja') }}</span>
          <span class="fan-presence-editor__slider-value"> {{ config.idle_speed }}% </span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="config.idle_speed"
          @input="update({ idle_speed: Number(($event.target as HTMLInputElement).value) })" />
      </div>

      <div class="fan-presence-editor__speed-slider">
        <div class="fan-presence-editor__slider-head">
          <span class="fan-presence-editor__label">{{ tt('Operating speed', 'Delovna hitrost') }}</span>
          <span class="fan-presence-editor__slider-value"> {{ config.operating_speed }}% </span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="config.operating_speed"
          @input="update({ operating_speed: Number(($event.target as HTMLInputElement).value) })" />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { FanPresenceTimerConfig, normalizeFanPresenceTimerConfig } from '../../../utils/fanAutomation';
import { tt } from '../../../utils/consoleText';

const props = defineProps<{
  modelValue: Record<string, unknown>;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, unknown>): void;
}>();

const config = computed<FanPresenceTimerConfig>(() => normalizeFanPresenceTimerConfig(props.modelValue));

function commit(next: FanPresenceTimerConfig) {
  emit('update:modelValue', JSON.parse(JSON.stringify(next)) as Record<string, unknown>);
}

function update(patch: Partial<FanPresenceTimerConfig>) {
  commit({ ...config.value, ...patch });
}
</script>

<style scoped>
.fan-presence-editor {
  width: 100%;
}

.fan-presence-editor__section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgb(100, 116, 139);
  margin-bottom: 8px;
}

.fan-presence-editor__hint {
  font-size: 11px;
  line-height: 1.45;
  color: rgb(148, 163, 184);
  margin-top: 6px;
}

.fan-presence-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.fan-presence-editor__number {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgb(148, 163, 184);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  padding: 6px 8px;
}

.fan-presence-editor__number input {
  flex: 1;
  min-width: 0;
  background: transparent;
  color: inherit;
  font-size: 12px;
  text-align: right;
}

.fan-presence-editor__speed-slider {
  margin-top: 10px;
}

.fan-presence-editor__slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fan-presence-editor__label {
  font-size: 11px;
  color: rgb(148, 163, 184);
}

.fan-presence-editor__slider-value {
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.fan-presence-editor__speed-slider input[type='range'] {
  width: 100%;
  accent-color: rgb(56, 189, 248);
}
</style>
